/**
 * App-shell provider for the global "New venue" entry point. Owns whether the
 * Add venue drawer is open, so the command palette and the "c then v" shortcut
 * can open it from any page, and sends the viewer to the new venue once it is
 * created.
 */
import { createContext, useCallback, useContext, useMemo, useState } from 'react';
import type { ReactNode } from 'react';
import { useNavigate } from 'react-router-dom';

import { useCreateShortcut } from '../../../hooks/use-create-shortcut';
import { usePermissions } from '../../../hooks/usePermissions';
import { Permission } from '../../../types/permissions';

import { venueHref } from './roster/venue-routes';
import VenueCreateDrawer from './VenueCreateDrawer';

/** One kbd chip per keystroke in the palette. */
export const NEW_VENUE_SHORTCUT_KEYS = ['C', 'V'] as const;

interface LauncherValue {
  openCreateVenue: () => void;
  /** Mirrors the venue create endpoint's own grant check. */
  canCreateVenue: boolean;
}

const CreateVenueLauncherContext = createContext<LauncherValue>({
  openCreateVenue: () => {},
  canCreateVenue: false,
});

export function CreateVenueLauncherProvider({ children }: { children: ReactNode }) {
  const { hasAnyPermission, loading } = usePermissions();
  const canCreateVenue =
    !loading && hasAnyPermission(Permission.WRITE_VENUES, Permission.WRITE_ALL);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  const openCreateVenue = useCallback(() => {
    if (canCreateVenue) setOpen(true);
  }, [canCreateVenue]);

  useCreateShortcut('v', canCreateVenue, () => setOpen(true));

  const value = useMemo(
    () => ({ openCreateVenue, canCreateVenue }),
    [openCreateVenue, canCreateVenue],
  );

  return (
    <CreateVenueLauncherContext.Provider value={value}>
      {children}
      {open && (
        <VenueCreateDrawer
          onClose={() => setOpen(false)}
          onCreated={(venue) => {
            setOpen(false);
            navigate(venueHref(venue));
          }}
        />
      )}
    </CreateVenueLauncherContext.Provider>
  );
}

export function useCreateVenueLauncher(): LauncherValue {
  return useContext(CreateVenueLauncherContext);
}
